import React, { useMemo } from "react";
import { ExternalLink, Minus, Plus, Clock } from "lucide-react";

import { Button } from "@/components/ui/button";
import { formatTotal, getProductAvailability } from "@/lib/utils";

// ----------------------------------------------------------------------------

interface OrderItemCardProps {
    id: string;
    code: string;
    title?: string;
    name?: string;
    attributes: {
        color: string;
        temperature: string;
        size: string;
    };
    quantity: number;
    price: number;
    originalPrice?: number;
    discountPercent?: number;
    imageUrl?: string;
    tags?: string[];
    stockCount?: number;
    unlimited?: number;
    note?: string;
    onRemove: (id: string) => void;
    onUpdateQuantity: (id: string, newQuantity: number) => void;
    onUpdateNote?: (id: string, note: string) => void;
    className?: string;
}

export function OrderItemCard(props: OrderItemCardProps) {
    // -------------------------------------
    // Props destructuring
    // -------------------------------------

    const {
        id,
        code,
        title,
        name,
        attributes,
        quantity,
        price,
        originalPrice,
        discountPercent,
        imageUrl,
        tags,
        stockCount,
        unlimited,
        note,
        onRemove,
        onUpdateQuantity,
        onUpdateNote,
        className,
    } = props;

    // -------------------------------------
    // Hooks (e.g. useState, useMemo ...)
    // -------------------------------------

    const [imageError, setImageError] = React.useState(false);

    // -------------------------------------
    // Derived availability
    // -------------------------------------

    const availability = useMemo(
        () => getProductAvailability({ tags, stockCount, unlimited }),
        [tags, stockCount, unlimited],
    );

    // -------------------------------------
    // Effects
    // -------------------------------------

    React.useEffect(() => {
        setImageError(false);
    }, [imageUrl, code]);

    // -------------------------------------
    // Component functions
    // -------------------------------------

    // -------------------------------------
    // Component local variables
    // -------------------------------------

    const lineTotal = price * quantity;
    const formattedLineTotal = formatTotal(lineTotal);
    const hasDiscount = discountPercent !== undefined && discountPercent > 0;
    const maxAllowedQty =
        typeof availability.availableQuantity === "number" &&
        availability.availableQuantity > 0
            ? availability.availableQuantity
            : undefined;

    return (
        <div
            className={`flex flex-col items-start justify-between p-4 border border-gray-200 rounded-lg bg-white gap-4 ${className || ""}`}>
            {/* Product Info */}
            <div className="flex items-start sm:items-center gap-4 w-full">
                <div className="w-16 h-16 bg-gray-200 rounded-md overflow-hidden flex-shrink-0">
                    {imageUrl && !imageError ? (
                        <img
                            src={imageUrl}
                            alt={code}
                            className="w-full h-full object-cover"
                            onError={() => setImageError(true)}
                        />
                    ) : (
                        <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center text-xs font-bold text-gray-400">
                            N/D
                        </div>
                    )}
                </div>

                <div className="flex flex-col min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-900 text-lg truncate">
                            {code}
                        </span>
                    </div>
                    {(title || name) && (title || name) !== code && (
                        <p className="text-xs text-gray-500 font-medium truncate mt-0.5">
                            {title || name}
                        </p>
                    )}

                    <div className="text-xs text-gray-500 leading-tight mt-1 space-y-0.5">
                        {attributes.color &&
                            attributes.color !== "Standard" && (
                                <p>{attributes.color}</p>
                            )}
                        {attributes.temperature &&
                            attributes.temperature !== "Standard" && (
                                <p>{attributes.temperature}</p>
                            )}
                        {attributes.size && attributes.size !== "Standard" && (
                            <p>{attributes.size}</p>
                        )}
                    </div>

                    {/* Availability Info in Order Summary */}
                    {availability.caseType === "restocking" && (
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold border bg-amber-50 text-amber-900 border-amber-200">
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-600" />
                                In riassortimento — Data presunto arrivo:{" "}
                                <strong className="font-bold text-amber-900">
                                    {availability.restockDate || "In attesa"}
                                </strong>
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* Controls & Price */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 w-full">
                {/* Quantity */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between sm:justify-start gap-2 sm:gap-4 w-full sm:w-auto">
                    <div className="flex items-center justify-between sm:justify-start gap-3 w-full sm:w-auto">
                        <span className="text-sm text-gray-500">Quantità</span>
                        <div className="flex items-center border border-gray-200 rounded-md h-10 bg-white overflow-hidden">
                            <Button
                                variant="ghost"
                                size="icon-sm"
                                aria-label="Decrease quantity"
                                className="rounded-none h-10 w-10"
                                onClick={() =>
                                    onUpdateQuantity(id, quantity - 1)
                                }
                                disabled={quantity <= 1}>
                                <Minus className="w-4 h-4" />
                            </Button>
                            <span className="w-10 text-center text-sm font-medium text-gray-900">
                                {quantity}
                            </span>
                            <Button
                                variant="ghost"
                                size="icon-sm"
                                aria-label="Increase quantity"
                                className="rounded-none h-10 w-10"
                                onClick={() =>
                                    onUpdateQuantity(id, quantity + 1)
                                }
                                disabled={
                                    maxAllowedQty !== undefined &&
                                    quantity >= maxAllowedQty
                                }>
                                <Plus className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                    {maxAllowedQty !== undefined && (
                        <div className="text-xs text-gray-500 self-end sm:self-auto bg-gray-50 px-2 py-1 rounded border border-gray-100">
                            Quantità ordinabile:{" "}
                            <span className="font-semibold text-gray-700">
                                {Math.max(0, maxAllowedQty - quantity)}
                            </span>
                        </div>
                    )}
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center sm:ml-auto gap-3 w-full sm:w-auto">
                    {/* Price */}
                    <div className="flex items-center justify-between sm:justify-end gap-2 sm:min-w-[130px]">
                        <span className="text-sm text-gray-500 sm:hidden">
                            Prezzo
                        </span>
                        <div className="flex flex-col sm:items-end">
                            <div className="flex items-center gap-1.5">
                                <span className="font-bold text-gray-900 text-base">
                                    {formattedLineTotal}
                                </span>
                            </div>
                            {hasDiscount && originalPrice ? (
                                <span className="text-xs text-gray-400 line-through">
                                    {formatTotal(originalPrice * quantity)}
                                </span>
                            ) : quantity > 1 ? (
                                <span className="text-xs text-gray-400">
                                    {formatTotal(price)}
                                </span>
                            ) : null}
                        </div>
                    </div>

                    {/* Actions */}
                    <Button
                        variant="outline"
                        onClick={() => onRemove(id)}
                        className="w-full sm:w-auto text-gray-700 border-gray-200 h-10">
                        Elimina
                    </Button>
                </div>
            </div>

            {/* Product Item Note */}
            <div className="w-full pt-1 border-t border-gray-100">
                {onUpdateNote ? (
                    <div className="flex flex-col gap-1">
                        <label
                            htmlFor={`note-${id}`}
                            className="text-[11px] font-medium text-gray-500">
                            Note per questo articolo:
                        </label>
                        <input
                            id={`note-${id}`}
                            type="text"
                            value={note || ""}
                            onChange={(e) => onUpdateNote(id, e.target.value)}
                            placeholder="Es. Richiesta finitura speciale, note tecniche..."
                            className="w-full text-xs rounded border border-gray-200 bg-gray-50/50 px-2.5 py-1.5 text-gray-900 placeholder:text-gray-400 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        />
                    </div>
                ) : note ? (
                    <div className="text-xs text-gray-600 bg-gray-50 rounded p-2 border border-gray-100">
                        <span className="font-semibold text-gray-700">
                            Nota:
                        </span>{" "}
                        {note}
                    </div>
                ) : null}
            </div>
        </div>
    );
}
